import { alpha, Badge, Box, Icon, IconButton, Paper, ThemeProvider, Tooltip, useTheme } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import FerniePopoutWindow from "~/components/FerniePopoutWindow";
import FernieConversation from "~/components/fernie/FernieConversation";
import FernieHistoryList from "~/components/fernie/FernieHistoryList";
import FernieHomeWindow from "~/components/fernie/FernieHomeWindow";
import FernieInlineLockup from "~/components/fernie/FernieInlineLockup";
import FernieRequestsList from "~/components/fernie/FernieRequestsList";
import FernieRequestsToggle from "~/components/fernie/FernieRequestsToggle";
import fernieButtonSx from "~/components/fernie/fernieButtonSx";
import { CaptionText } from "~/components/Typographies";
import { gateSentinelSx } from "~/components/walkthrough/anchorResolution";
import { FeatureFlagName } from "~/gql/graphqlBase.generated";
import resolveFerniePanelSizing from "~/helpers/aiChat/ferniePanelSizing";
import openFernieWindow, { type FernieWindowOpenResult } from "~/helpers/aiChat/openFernieWindow";
import { useAppDispatch, useAppSelector } from "~/hooks/reduxHooks";
import useFeatureFlag from "~/hooks/useFeatureFlag";
import useFernieChat from "~/hooks/useFernieChat";
import useFernieRequests from "~/hooks/useFernieRequests";
import useMobileKeyboardViewport from "~/hooks/useMobileKeyboardViewport";
import {
	AI_PANEL_WIDTH,
	clearFerniePrompt,
	closeAiPanel,
	closeAiPanelPopout,
	openAiPanelPopout
} from "~/store/layoutSlice";
import { createFernieBrandTheme } from "~/themes/fernieBrand";
import { TOUR_ANCHORS } from "~/walkthroughs/registry";

interface AiAssistantProps {
	homeSlot?: HTMLElement | null;
}

const AiAssistant = ({ homeSlot = null }: AiAssistantProps) => {
	const { t } = useTranslation("common");
	const dispatch = useAppDispatch();
	const outerTheme = useTheme();
	const open = useAppSelector((state) => state.layout.aiPanelOpen);
	const poppedOut = useAppSelector((state) => state.layout.aiPanelPoppedOut);
	const ferniePrompt = useAppSelector((state) => state.layout.ferniePrompt);

	const chat = useFernieChat({ focusOnOpen: open, isActive: open || homeSlot !== null });
	const { isEnabled: requestsEnabled } = useFeatureFlag(FeatureFlagName.FernieRequestsTab);
	const requests = useFernieRequests(chat, open, requestsEnabled);
	const openRequests = requests.isAvailable ? () => chat.setView("requests") : undefined;

	const keyboardViewport = useMobileKeyboardViewport(open && !poppedOut);
	const panelSizing = resolveFerniePanelSizing({ keyboardViewport, poppedOut });

	const [opening, setOpening] = useState<FernieWindowOpenResult | null>(null);
	const brandTheme = useMemo(() => createFernieBrandTheme(outerTheme, { shouldApplyBrandFont: false }), [outerTheme]);

	useEffect(() => {
		if (poppedOut && !opening) dispatch(closeAiPanelPopout());
	}, [opening, poppedOut]);

	useEffect(() => {
		if (!ferniePrompt || chat.loading) return;
		dispatch(clearFerniePrompt());
		chat.setView("chat");
		void chat.send(ferniePrompt.text, { keepComposer: true, opensOnboarding: ferniePrompt.opensOnboarding });
	}, [chat.loading, ferniePrompt]);

	const handleClose = () => {
		setOpening(null);
		dispatch(closeAiPanel());
	};

	const handlePopOut = () => {
		setOpening(openFernieWindow(brandTheme.palette.background.default));
		dispatch(openAiPanelPopout());
	};

	const handleDock = () => {
		setOpening(null);
		dispatch(closeAiPanelPopout());
	};

	const panel = open ? (
		<Paper
			aria-label={t("fernieChat.panel.windowTitle")}
			component="aside"
			elevation={0}
			sx={{
				backgroundColor: "background.default",
				borderLeft: poppedOut ? "none" : "1px solid",
				borderLeftColor: "divider",
				borderRadius: 0,
				display: "flex",
				flexDirection: "column",
				fontFamily: (theme) => theme.typography.fontFamily,
				height: panelSizing.height,
				minWidth: 0,
				overflow: "hidden",
				position: poppedOut ? "relative" : "fixed",
				right: poppedOut ? "auto" : 0,
				top: panelSizing.top,
				width: poppedOut ? "100%" : { sm: AI_PANEL_WIDTH, xs: "100vw" },
				zIndex: poppedOut ? "auto" : 1200
			}}
		>
			<Box
				sx={{
					alignItems: "center",
					backgroundColor: "background.default",
					display: "flex",
					flexShrink: 0,
					justifyContent: "space-between",
					minHeight: 52,
					px: 2
				}}
			>
				<Box sx={{ alignItems: "center", display: "flex", gap: 1 }}>
					<FernieInlineLockup
						nameColor="text.primary"
						size={32}
						state="rest"
						sx={{ "& > .MuiTypography-root": { fontSize: 16 }, gap: 0.75 }}
					/>
					<CaptionText
						bold
						sx={{
							backgroundColor: (theme) => alpha(theme.palette.fernie.fern, 0.07),
							borderRadius: "4px",
							color: "text.secondary",
							fontSize: "0.55rem",
							letterSpacing: "0.04em",
							lineHeight: 1.5,
							px: 0.75,
							py: 0.25,
							textTransform: "uppercase"
						}}
					>
						{t("fernieChat.panel.beta")}
					</CaptionText>
				</Box>
				<Box
					sx={{
						"& .MuiIconButton-root": { ...fernieButtonSx.focusRing, borderRadius: "10px", height: 32, width: 32 },
						display: "flex",
						gap: 0.25
					}}
				>
					<Tooltip title={t("fernieChat.panel.newChat")}>
						<IconButton
							onClick={chat.handleNewChat}
							size="small"
							sx={{ "&:hover": { backgroundColor: "action.hover" }, color: "text.secondary" }}
						>
							<Icon sx={{ fontSize: 18 }}>edit_square</Icon>
						</IconButton>
					</Tooltip>
					<Tooltip title={chat.view === "history" ? t("fernieChat.panel.backToChat") : chat.historyLabel}>
						<IconButton
							onClick={() => chat.setView((v) => (v === "history" ? "chat" : "history"))}
							size="small"
							sx={{
								"&:hover": { backgroundColor: "action.hover" },
								backgroundColor: chat.view === "history" ? "action.selected" : "transparent",
								color: chat.view === "history" ? "text.primary" : "text.secondary"
							}}
						>
							<Badge
								color="primary"
								invisible={chat.view === "history" || !chat.hasHistory}
								sx={{ "& .MuiBadge-badge": { height: 8, minWidth: 8 } }}
								variant="dot"
							>
								<Icon sx={{ fontSize: 18 }}>schedule</Icon>
							</Badge>
						</IconButton>
					</Tooltip>
					{requests.isAvailable && (
						<FernieRequestsToggle
							active={chat.view === "requests"}
							onClick={() => chat.setView((view) => (view === "requests" ? "chat" : "requests"))}
						/>
					)}
					<Tooltip title={poppedOut ? t("fernieChat.panel.dockPanel") : t("fernieChat.panel.popOut")}>
						<IconButton
							onClick={poppedOut ? handleDock : handlePopOut}
							size="small"
							sx={{ "&:hover": { backgroundColor: "action.hover" }, color: "text.secondary" }}
						>
							<Icon sx={{ fontSize: 18 }}>{poppedOut ? "picture_in_picture" : "open_in_new"}</Icon>
						</IconButton>
					</Tooltip>
					{!poppedOut && (
						<Tooltip title={t("fernieChat.panel.close")}>
							<IconButton
								onClick={handleClose}
								size="small"
								sx={{ "&:hover": { backgroundColor: "action.hover" }, color: "text.secondary" }}
							>
								<Icon sx={{ fontSize: 18 }}>close</Icon>
							</IconButton>
						</Tooltip>
					)}
				</Box>
			</Box>

			{chat.view === "requests" && requests.isAvailable ? (
				<FernieRequestsList requests={requests} />
			) : chat.view === "history" ? (
				<FernieHistoryList chat={chat} />
			) : (
				<>
					<Box data-tour-id={TOUR_ANCHORS.fernieChatView} sx={gateSentinelSx} />
					<FernieConversation chat={chat} onOpenRequests={openRequests} />
				</>
			)}
		</Paper>
	) : null;

	return (
		<ThemeProvider theme={brandTheme}>
			{open && poppedOut && opening && (
				<FerniePopoutWindow onBlocked={handleDock} onClose={handleClose} opening={opening}>
					{panel}
				</FerniePopoutWindow>
			)}
			{open && !poppedOut && panel}
			{homeSlot && !open && createPortal(<FernieHomeWindow chat={chat} />, homeSlot)}
		</ThemeProvider>
	);
};

export default AiAssistant;
